import { useCallback } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type {
  GlobalMenuItems} from '../store/globalContextMenuStore';
import {
  useGlobalContextMenuStore,
} from '../store/globalContextMenuStore';

type NavigationEventTarget = HTMLElement;
type NavigationMouseEvent = ReactMouseEvent<NavigationEventTarget> | MouseEvent;

const INTERACTIVE_CHILD_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  'summary',
  'label',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
  '[onclick]',
].join(',');

const getTargetElement = ({
  eventTarget,
}: {
  eventTarget: EventTarget | null;
}): HTMLElement | null => {
  if (eventTarget instanceof HTMLElement) {
    return eventTarget;
  }

  if (eventTarget instanceof Node) {
    return eventTarget.parentElement;
  }

  return null;
};

const hasClickableChildAncestor = ({
  targetElement,
  currentTarget,
}: {
  targetElement: HTMLElement | null;
  currentTarget: EventTarget | null;
}) => {
  if (!targetElement || !(currentTarget instanceof HTMLElement)) {
    return false;
  }

  const interactiveAncestor = targetElement.closest(INTERACTIVE_CHILD_SELECTOR);
  if (
    interactiveAncestor &&
    interactiveAncestor !== currentTarget &&
    currentTarget.contains(interactiveAncestor)
  ) {
    return true;
  }

  return false;
};

export const useGlobalContextMenu = () => {
  const openContextMenu = useCallback(
    ({
      event,
      items,
    }: {
      event: NavigationMouseEvent;
      items: GlobalMenuItems[];
    }) => {
      if (!event || !items || items.length === 0) {
        return;
      }

      const eventTarget = event.target;
      const currentTarget = event.currentTarget;
      const targetElement = getTargetElement({ eventTarget });

      if (
        hasClickableChildAncestor({
          targetElement,
          currentTarget,
        })
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      useGlobalContextMenuStore.getState().openMenu({
        x: event.clientX,
        y: event.clientY,
        items,
      });
    },
    [],
  );

  return {
    openContextMenu,
  };
};
